<template>
  <section class="tb-sync-layout">
    <header class="tb-sync-topbar">
      <div class="tb-sync-topbar-title">
        <span class="pulse-dot" :class="{ 'is-off': !axdEnabled }"></span>
        奥讯达基础信息同步
      </div>
      <nav class="tb-sync-tabs">
        <button
          v-for="tab in tabList"
          :key="tab.key"
          type="button"
          class="tb-sync-tab"
          :class="{ 'is-active': activeTab === tab.key }"
          @click="onModuleSelect(tab.key)"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
        </button>
      </nav>
    </header>

    <main class="tb-sync-main">
      <div class="query-layout">
        <div class="dept-filter-pane">
          <div class="dept-filter-header">
            <i class="el-icon-school"></i>
            <span>选择机构 / 学校</span>
          </div>
          <!-- 奥讯达仅对接家校年级/班级/学生/家长，固定家校树。最后更新：2026-08-30 -->
          <DepartmentPane
            :key="'axd-teaching'"
            directory-scope="teaching"
            pane-title="家校通讯录"
            :default-select-root="true"
            @tree-node-click="onTreeNodeClick"
          />
        </div>

        <section class="query-main">
          <el-card shadow="never" class="step-card branch-card config-card" :class="{ 'is-config-collapsed': !configExpanded }">
            <div slot="header" class="card-header-styled action-header config-card-header" @click="toggleConfigPanel">
              <div class="action-header-left">
                <i class="el-icon-key"></i>
                <span>学校奥讯达配置</span>
                <el-tag
                  v-if="schoolDepartmentId"
                  size="mini"
                  :type="isAxdConfigured ? 'success' : 'info'"
                  effect="plain"
                  class="config-status-tag"
                >
                  {{ isAxdConfigured ? '已配置' : '未配置' }}
                </el-tag>
                <span v-if="!configExpanded && isAxdConfigured" class="config-collapsed-brief">
                  sid {{ displayVendorSid }} · {{ configForm.appId || '—' }} · {{ axdEnabled ? '已启用' : '未启用' }}
                </span>
              </div>
              <div class="config-header-right">
                <span v-if="!isSchoolNode && schoolDepartmentId" class="config-hint-muted" @click.stop>
                  编辑配置需选中学校节点
                </span>
                <i
                  class="config-collapse-icon"
                  :class="configExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
                  :title="configExpanded ? '收起' : '展开'"
                />
              </div>
            </div>
            <div v-show="configExpanded" class="config-card-body">
            <div v-if="!schoolDepartmentId" class="notice-banner notice-info config-notice">
              <i class="el-icon-info"></i>
              <span>请先从左侧选择机构或学校节点</span>
            </div>
            <el-form
              v-else
              ref="configFormRef"
              :model="configForm"
              label-width="88px"
              size="small"
              class="config-form axd-config-form"
              v-loading="configLoading"
            >
              <div class="axd-config-section">
                <div class="axd-config-section-title">连接信息</div>
                <el-row :gutter="16">
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-form-item label="厂家 sid" required>
                      <el-input
                        v-model="configForm.vendorSid"
                        :disabled="!isSchoolNode"
                        placeholder="奥讯达学校 sid"
                        clearable
                        autocomplete="off"
                        autocorrect="off"
                        spellcheck="false"
                        name="axd-vendor-sid"
                      />
                    </el-form-item>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-form-item label="应用 ID" required>
                      <el-input
                        v-model="configForm.appId"
                        :disabled="!isSchoolNode"
                        placeholder="app_id"
                        clearable
                        autocomplete="off"
                        autocorrect="off"
                        spellcheck="false"
                        name="axd-app-id"
                      />
                    </el-form-item>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-form-item label="启用">
                      <el-switch
                        v-model="configForm.isEnabled"
                        :disabled="!isSchoolNode"
                        active-text="已启用"
                        inactive-text="未启用"
                      />
                    </el-form-item>
                  </el-col>
                  <el-col :xs="24" :md="16">
                    <el-form-item label="API 地址">
                      <el-input
                        v-model="configForm.apiBaseUrl"
                        :disabled="!isSchoolNode"
                        placeholder="http://api.hxaxd.com"
                        clearable
                      />
                    </el-form-item>
                  </el-col>
                  <el-col :xs="24" :md="16">
                    <el-form-item label="SSO 地址">
                      <el-input
                        v-model="configForm.ssoPortalUrl"
                        :disabled="!isSchoolNode"
                        placeholder="https://edu.hxaxd.com/api/user/sso"
                        clearable
                      />
                    </el-form-item>
                  </el-col>
                </el-row>
              </div>

              <div class="axd-config-section axd-config-secrets">
                <div class="axd-config-section-title">密钥凭证</div>
                <p class="axd-config-secrets-tip">默认圆点隐藏，点击右侧图标可查看完整内容；长密钥会自动换行显示。</p>

                <el-form-item label="应用密钥" required class="axd-secret-form-item">
                  <div class="axd-secret-input-wrap">
                    <el-input
                      v-model="configForm.appKey"
                      type="textarea"
                      :autosize="{ minRows: 1, maxRows: 4 }"
                      :disabled="!isSchoolNode"
                      resize="none"
                      clearable
                      autocomplete="new-password"
                      autocorrect="off"
                      spellcheck="false"
                      name="axd-app-key"
                      placeholder="app_key"
                      :class="{ 'axd-secret-masked': !showAppKey }"
                      class="axd-secret-textarea"
                    />
                    <button
                      type="button"
                      class="axd-secret-toggle-btn"
                      :disabled="!isSchoolNode"
                      title="显示/隐藏"
                      @click="showAppKey = !showAppKey"
                    >
                      <i :class="showAppKey ? 'el-icon-view' : 'el-icon-minus'" />
                    </button>
                  </div>
                </el-form-item>

                <el-form-item label="单点密钥" class="axd-secret-form-item">
                  <div class="axd-secret-input-wrap">
                    <el-input
                      v-model="configForm.ssoKey"
                      type="textarea"
                      :autosize="{ minRows: 1, maxRows: 4 }"
                      :disabled="!isSchoolNode"
                      resize="none"
                      clearable
                      autocomplete="new-password"
                      autocorrect="off"
                      spellcheck="false"
                      name="axd-sso-key"
                      placeholder="ssokey（可选）"
                      :class="{ 'axd-secret-masked': !showSsoKey }"
                      class="axd-secret-textarea"
                    />
                    <button
                      type="button"
                      class="axd-secret-toggle-btn"
                      :disabled="!isSchoolNode"
                      title="显示/隐藏"
                      @click="showSsoKey = !showSsoKey"
                    >
                      <i :class="showSsoKey ? 'el-icon-view' : 'el-icon-minus'" />
                    </button>
                  </div>
                </el-form-item>
              </div>

              <div class="config-actions">
                <el-button
                  size="small"
                  icon="el-icon-connection"
                  :loading="configTesting"
                  :disabled="!isSchoolNode"
                  @click="testConnection"
                >
                  测试连通
                </el-button>
                <el-button
                  type="primary"
                  size="small"
                  icon="el-icon-check"
                  :loading="configSaving"
                  :disabled="!isSchoolNode"
                  @click="saveConfig"
                >
                  保存
                </el-button>
              </div>
            </el-form>
            </div>
          </el-card>

          <div class="sync-head">
            <div class="sync-head-row">
              <div class="sync-metrics">
                <div
                  class="stat-card stat-emerald"
                  :class="{ 'stat-card-clickable': canClickStatCard('synced') }"
                  @click="openStatsDetail('synced')"
                >
                  <span class="stat-label">已同步</span>
                  <span class="stat-value">{{ activeStats.synced }}<span class="unit">条</span></span>
                </div>
                <div
                  class="stat-card stat-orange"
                  :class="{ 'stat-card-clickable': canClickStatCard('unsynced') }"
                  @click="openStatsDetail('unsynced')"
                >
                  <span class="stat-label">未同步</span>
                  <span class="stat-value">{{ activeStats.unsynced }}<span class="unit">条</span></span>
                </div>
                <div
                  class="stat-card stat-blue"
                  :class="{ 'stat-card-clickable': canClickStatCard('total') }"
                  @click="openStatsDetail('total')"
                >
                  <span class="stat-label">总数</span>
                  <span class="stat-value">{{ activeStats.total }}<span class="unit">条</span></span>
                </div>
              </div>
              <div class="status-strip">
                <div class="status-chip">
                  <span>当前机构</span>
                  <strong>{{ currentDepartmentName || '未选择' }}</strong>
                </div>
                <div class="status-chip" :class="axdEnabled ? 'chip-success' : 'chip-muted'">
                  <span>奥讯达同步</span>
                  <strong>{{ axdEnabled ? '已启用' : '未启用' }}</strong>
                </div>
                <div v-if="displayVendorSid" class="status-chip">
                  <span>厂家 sid</span>
                  <strong>{{ displayVendorSid }}</strong>
                </div>
              </div>
              <div class="sync-head-tools">
                <el-button size="small" plain icon="el-icon-question" @click="guideVisible = true">说明</el-button>
                <el-button
                  type="primary"
                  size="small"
                  icon="el-icon-connection"
                  :loading="syncLoading[activeTab]"
                  :disabled="!canTriggerAction(activeTab)"
                  @click="triggerSync(activeTab)"
                >同步{{ activeTabLabel }}</el-button>
                <el-button
                  type="danger"
                  plain
                  size="small"
                  icon="el-icon-delete"
                  :loading="deleteLoading[activeTab]"
                  :disabled="!canTriggerDelete(activeTab)"
                  @click="triggerDelete(activeTab)"
                >从奥讯达删除</el-button>
                <el-button size="small" plain icon="el-icon-refresh" title="刷新统计" @click="loadStats(activeTab, true)"></el-button>
              </div>
            </div>
            <div v-if="!currentDepartmentId" class="sync-head-tip">
              <i class="el-icon-info"></i>
              <span>请先从左侧选择机构或学校节点</span>
            </div>
            <div v-else-if="!axdEnabled" class="sync-head-tip is-warning">
              <i class="el-icon-warning-outline"></i>
              <span>当前学校未配置或未启用奥讯达同步，请先完成上方密钥配置并启用</span>
            </div>
            <div v-else-if="headTip" class="sync-head-tip">
              <i class="el-icon-info"></i>
              <span>{{ headTip }}</span>
            </div>
          </div>

          <el-card shadow="never" class="step-card branch-card log-card">
            <div slot="header" class="card-header-styled">
              <i class="el-icon-tickets"></i>
              <span>同步记录</span>
            </div>
            <AxdSyncLogPanel
              ref="logPanel"
              :key="`${activeTab}-${currentDepartmentId || 0}`"
              :sync-type="activeTab"
              :root-department-id="currentDepartmentId"
              @history-loaded="(log) => onHistoryLoaded(activeTab, log)"
              @sync-completed="onSyncCompleted(activeTab)"
            />
          </el-card>
        </section>
      </div>
    </main>

    <el-dialog
      :visible.sync="guideVisible"
      title="奥讯达同步说明"
      width="560px"
      append-to-body
      custom-class="sync-guide-dialog"
    >
      <div class="guide-block">
        <span class="desc-badge badge-primary">推荐顺序</span>
        <p class="guide-text">① 配置密钥 → ② 同步部门架构（家校年级/班级）→ ③ 同步学生 → ④ 同步家长</p>
      </div>
      <div class="guide-block">
        <span class="desc-badge badge-warning">当前页签</span>
        <p v-if="activeTab === 'dept'" class="guide-text">使用家校通讯录。选中节点下的年级与班级会一并纳入同步。奥讯达没有内部部门接口。</p>
        <p v-else-if="activeTab === 'student'" class="guide-text">请先完成家校部门架构同步。所属班级未同步的学生会跳过。</p>
        <p v-else class="guide-text">班级未同步则跳过。班级已同步时，未同步的学生会自动补同步后再推家长。</p>
      </div>
      <div class="guide-block">
        <span class="desc-badge badge-warning">删除说明</span>
        <p class="guide-text">「从奥讯达删除」只清除厂家侧数据和同步映射，不删除本地部门、学生或家长。统计明细里也可以逐条操作。</p>
      </div>
    </el-dialog>

    <el-dialog
      :title="statsDetailTitle"
      :visible.sync="statsDetailVisible"
      width="1020px"
      top="8vh"
      append-to-body
      custom-class="tb-sync-detail-dialog"
      @closed="onStatsDetailClosed"
      @opened="layoutStatsDetailTable"
    >
      <div class="stats-detail-meta">
        <span><i class="el-icon-location-outline"></i> 当前机构：{{ currentDepartmentName || '' }}</span>
        <span v-if="statsDetailTip" class="stats-detail-tip"><i class="el-icon-info"></i> {{ statsDetailTip }}</span>
      </div>
      <div class="stats-detail-toolbar">
        <el-input
          v-model="statsDetailKeyword"
          size="small"
          clearable
          :placeholder="statsDetailSearchPlaceholder"
          prefix-icon="el-icon-search"
          class="stats-detail-search-input"
          @keyup.enter.native="onStatsDetailSearch"
          @clear="onStatsDetailSearch"
        />
        <el-button size="small" type="primary" plain icon="el-icon-search" @click="onStatsDetailSearch">搜索</el-button>
      </div>
      <div class="stats-detail-table-wrap">
        <el-table
          ref="statsDetailTable"
          :data="statsDetailList"
          border
          size="small"
          :height="statsDetailTableHeight"
          v-loading="statsDetailLoading"
          class="custom-table dialog-table stats-detail-table"
        >
          <el-table-column
            v-for="col in statsDetailColumns"
            :key="col.prop"
            :prop="col.prop"
            :label="col.label"
            :width="col.width"
            :min-width="col.minWidth"
            :align="col.align"
            :header-align="col.headerAlign || col.align"
            :show-overflow-tooltip="shouldStatsDetailOverflowTooltip(col)"
          >
            <template slot-scope="scope">
              <template v-if="col.columnType === 'syncStatus'">
                <el-tag :type="scope.row.isSynced ? 'success' : 'warning'" size="mini">
                  {{ scope.row.isSynced ? '已同步' : '未同步' }}
                </el-tag>
              </template>
              <template v-else>
                <span
                  v-if="isAxdSyncVendorIdProp(col.prop) && formatAxdSyncVendorIdDisplay(scope.row[col.prop])"
                  class="detail-id-cell detail-id-cell-ellipsis"
                >
                  {{ formatAxdSyncVendorIdDisplay(scope.row[col.prop]) }}
                </span>
                <span
                  v-else-if="isAxdSyncEntityIdProp(col.prop) && formatAxdSyncCellDisplay(scope.row[col.prop])"
                  class="detail-id-cell detail-id-cell-ellipsis"
                >
                  {{ formatAxdSyncCellDisplay(scope.row[col.prop]) }}
                </span>
                <span v-else-if="!isAxdSyncVendorIdProp(col.prop) && !isAxdSyncEntityIdProp(col.prop)">
                  {{ formatAxdSyncCellDisplay(scope.row[col.prop]) }}
                </span>
              </template>
            </template>
          </el-table-column>
          <el-table-column label="操作" :width="statsDetailActionColumnWidth" align="center">
            <template slot-scope="scope">
              <div class="stats-detail-op-cell">
                <el-button
                  v-if="!scope.row.isSynced"
                  type="text"
                  size="small"
                  class="link-btn"
                  :loading="statsDetailItemLoading === getStatsDetailItemKey(scope.row)"
                  :disabled="!canStatsDetailRowAction(scope.row, 'sync')"
                  @click="statsDetailSyncRow(scope.row)"
                >
                  同步
                </el-button>
                <template v-else>
                  <el-button
                    v-if="statsDetailTabKey === 'student'"
                    type="text"
                    size="small"
                    class="link-btn"
                    :class="{ 'link-btn-disabled': !scope.row.hasUploadableHeadPhoto }"
                    :loading="statsDetailItemLoading === getStatsDetailItemKey(scope.row)"
                    :disabled="!canStatsDetailRowAction(scope.row, 'uploadPhoto')"
                    @click="statsDetailUploadPhotoRow(scope.row)"
                  >
                    上传头像
                  </el-button>
                  <el-button
                    type="text"
                    size="small"
                    class="link-btn link-btn-danger"
                    :loading="statsDetailItemLoading === getStatsDetailItemKey(scope.row)"
                    :disabled="!canStatsDetailRowAction(scope.row, 'delete')"
                    @click="statsDetailDeleteRow(scope.row)"
                  >
                    删除
                  </el-button>
                </template>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="pagination-bar">
        <el-pagination
          small
          layout="total, prev, pager, next"
          :total="statsDetailTotal"
          :page-size="statsDetailPageSize"
          :current-page.sync="statsDetailPage"
          @current-change="fetchStatsDetailPage"
        />
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="statsDetailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import AxdSyncLogPanel from '../../components/AxdSync/AxdSyncLogPanel';
import { getDepartmentType } from '../../api/modules/department';
import { normalizeDeptId, deptIdsEqual } from '../../utils/deptCascaderProps';
import { getAxdSchoolConfig, saveAxdSchoolConfig, testAxdConnection, getAxdDeptStats, getAxdStudentStats, getAxdParentStats, getAxdDeptDetailPage, getAxdStudentDetailPage, getAxdParentDetailPage } from '../../api/modules/integration';
import {
  axdSyncApiMap,
  calcAxdCooldownRemainSeconds,
  formatAxdCooldownText,
  normalizeAxdSyncLogPayload,
  isAxdSyncEntityIdProp,
  isAxdSyncVendorIdProp,
  formatAxdSyncVendorIdDisplay,
  formatAxdSyncCellDisplay,
} from '../../components/AxdSync/axdSyncApiMap';

const DEFAULT_API_BASE_URL = 'http://api.hxaxd.com';
const DEFAULT_SSO_PORTAL_URL = 'https://edu.hxaxd.com/api/user/sso';

/**
 * 奥讯达基础信息同步统一页面
 * 路由建议：/User/AxdBasicSync
 * 创建于 2026-06-13
 */
export default {
  name: 'AxdBasicSync',
  components: { DepartmentPane, AxdSyncLogPanel },
  data() {
    return {
      activeTab: 'dept',
      guideVisible: false,
      currentDepartment: null,
      currentDepartmentId: null,
      currentDepartmentName: '',
      schoolInfo: null,
      schoolDepartmentId: null,
      axdConfig: null,
      configLoading: false,
      configSaving: false,
      configTesting: false,
      /** 学校奥讯达配置默认折叠。最后更新：2026-08-30 */
      configExpanded: false,
      showAppKey: false,
      showSsoKey: false,
      configForm: {
        vendorSid: '',
        appId: '',
        appKey: '',
        ssoKey: '',
        apiBaseUrl: DEFAULT_API_BASE_URL,
        ssoPortalUrl: DEFAULT_SSO_PORTAL_URL,
        isEnabled: true,
      },
      syncLoading: { dept: false, student: false, parent: false },
      deleteLoading: { dept: false, student: false, parent: false },
      deleteBlockReason: { dept: '', student: '', parent: '' },
      blockReason: { dept: '', student: '', parent: '' },
      cooldownTimer: null,
      cooldownRemain: { dept: 0, student: 0, parent: 0 },
      stats: {
        dept: { total: 0, synced: 0, unsynced: 0, tip: '', classNotMappedCount: 0, pendingDeleteCount: 0 },
        student: { total: 0, synced: 0, unsynced: 0, tip: '', classNotMappedCount: 0, pendingDeleteCount: 0 },
        parent: { total: 0, synced: 0, unsynced: 0, tip: '', classNotMappedCount: 0, pendingDeleteCount: 0 },
      },
      tabList: [
        { key: 'dept', label: '部门架构', icon: 'el-icon-office-building' },
        { key: 'student', label: '学生', icon: 'el-icon-user' },
        { key: 'parent', label: '家长', icon: 'el-icon-user-solid' },
      ],
      statsApiMap: {
        dept: getAxdDeptStats,
        student: getAxdStudentStats,
        parent: getAxdParentStats,
      },
      statsDetailVisible: false,
      statsDetailTabKey: 'student',
      statsDetailFilterType: 'total',
      statsDetailList: [],
      statsDetailTotal: 0,
      statsDetailPage: 1,
      statsDetailPageSize: 10,
      statsDetailLoading: false,
      statsDetailKeyword: '',
      statsDetailItemLoading: null,
      statFilterMap: { synced: 1, unsynced: 2, total: 0 },
      statLabelMap: { synced: '已同步', unsynced: '未同步', total: '全部' },
    };
  },
  computed: {
    activeTabLabel() {
      const tab = this.tabList.find((t) => t.key === this.activeTab);
      return tab ? tab.label : '';
    },
    activeStats() {
      return this.stats[this.activeTab] || {
        total: 0, synced: 0, unsynced: 0, tip: '', classNotMappedCount: 0,
      };
    },
    /** 顶栏第二行提示：拦截原因、跳过人数或统计说明。最后修改：2026-10-14 */
    headTip() {
      const stats = this.activeStats;
      if (this.blockReason[this.activeTab]) return this.blockReason[this.activeTab];
      if (this.deleteBlockReason[this.activeTab]) return this.deleteBlockReason[this.activeTab];
      if (this.activeTab === 'student' && stats.classNotMappedCount > 0) {
        return `${stats.classNotMappedCount} 名学生因班级未同步将被跳过`;
      }
      if (this.activeTab === 'parent' && stats.classNotMappedCount > 0) {
        return `${stats.classNotMappedCount} 名家长因学生未同步将被跳过`;
      }
      return stats.tip || '';
    },
    isSchoolNode() {
      return !!(this.schoolDepartmentId && this.currentDepartmentId
        && deptIdsEqual(this.currentDepartmentId, this.schoolDepartmentId));
    },
    currentDeptOrgType() {
      const d = this.currentDepartment;
      if (!d) return '';
      return String(d.DeptOrgType || d.deptOrgType || '').toLowerCase();
    },
    isAxdDeptSyncNode() {
      if (!this.currentDepartmentId) return false;
      const t = this.currentDeptOrgType;
      if (['school', 'campus', 'period', 'grade', 'class'].includes(t)) return true;
      // 树节点未带 DeptOrgType 时仍允许尝试，由后端按子树解析年级/班级
      return !t;
    },
    axdEnabled() {
      if (!this.axdConfig) return false;
      const enabled = this.axdConfig.isEnabled != null ? this.axdConfig.isEnabled : this.axdConfig.IsEnabled;
      return !!enabled;
    },
    isAxdConfigured() {
      return !!this.axdConfig;
    },
    displayVendorSid() {
      if (this.axdConfig) {
        return this.axdConfig.vendorSid || this.axdConfig.VendorSid || '';
      }
      return this.configForm.vendorSid || '';
    },
    statsDetailConfig() {
      return axdSyncApiMap[this.statsDetailTabKey] || axdSyncApiMap.student;
    },
    statsDetailActionColumnWidth() {
      return this.statsDetailTabKey === 'student' ? 148 : 88;
    },
    statsDetailColumns() {
      return (this.statsDetailConfig && this.statsDetailConfig.statsDetailColumns) || [];
    },
    statsDetailTitle() {
      const tab = this.tabList.find((t) => t.key === this.statsDetailTabKey);
      const tabLabel = tab ? tab.label : '';
      const filterLabel = this.statLabelMap[this.statsDetailFilterType] || '';
      return `${tabLabel} · ${filterLabel}明细`;
    },
    statsDetailTip() {
      if (this.statsDetailTabKey !== 'dept') return '';
      return (this.stats.dept && this.stats.dept.tip) || '';
    },
    statsDetailSearchPlaceholder() {
      const cfg = this.statsDetailConfig;
      return (cfg && cfg.statsDetailSearchPlaceholder) || '关键词';
    },
    statsDetailTableHeight() {
      const headerHeight = 40;
      const rowHeight = 42;
      return headerHeight + this.statsDetailPageSize * rowHeight;
    },
  },
  destroyed() {
    this.stopCooldownTimer();
  },
  methods: {
    isAxdSyncEntityIdProp,
    isAxdSyncVendorIdProp,
    formatAxdSyncVendorIdDisplay,
    formatAxdSyncCellDisplay,
    /** 统计明细：ID 列自带省略号，勿再开 overflow tooltip，避免空值悬停出现黑点气泡。 */
    shouldStatsDetailOverflowTooltip(col) {
      if (!col || col.columnType === 'syncStatus') return false;
      if (isAxdSyncVendorIdProp(col.prop) || isAxdSyncEntityIdProp(col.prop)) return false;
      return true;
    },
    toggleConfigPanel() {
      this.configExpanded = !this.configExpanded;
    },
    onModuleSelect(key) {
      if (this.activeTab === key) return;
      this.activeTab = key;
      this.$nextTick(() => {
        this.onTabChange();
      });
    },
    clearCurrentDepartment() {
      this.currentDepartment = null;
      this.currentDepartmentId = null;
      this.currentDepartmentName = '';
      this.schoolInfo = null;
      this.schoolDepartmentId = null;
      this.axdConfig = null;
      this.resetConfigForm();
    },
    onTreeNodeClick(data) {
      this.currentDepartment = data;
      this.currentDepartmentId = data && data.value ? normalizeDeptId(data.value) : null;
      this.currentDepartmentName = (data && (data.label || data.name)) || '';
      this.loadDepartmentInfo();
    },
    loadDepartmentInfo() {
      if (!this.currentDepartmentId) {
        this.schoolInfo = null;
        this.schoolDepartmentId = null;
        this.axdConfig = null;
        this.resetConfigForm();
        return;
      }
      getDepartmentType({ id: this.currentDepartmentId }).then((res) => {
        if (res.data.success && res.data.response) {
          this.schoolInfo = res.data.response;
          this.schoolDepartmentId = normalizeDeptId(res.data.response.Id);
        } else {
          this.schoolInfo = null;
          this.schoolDepartmentId = null;
        }
        this.loadAxdConfig();
        this.refreshAll();
      });
    },
    resetConfigForm() {
      this.showAppKey = false;
      this.showSsoKey = false;
      this.configForm = {
        vendorSid: '',
        appId: '',
        appKey: '',
        ssoKey: '',
        apiBaseUrl: DEFAULT_API_BASE_URL,
        ssoPortalUrl: DEFAULT_SSO_PORTAL_URL,
        isEnabled: true,
      };
    },
    applyConfigToForm(cfg) {
      if (!cfg) {
        this.resetConfigForm();
        return;
      }
      this.showAppKey = false;
      this.showSsoKey = false;
      this.configForm = {
        vendorSid: cfg.vendorSid || cfg.VendorSid || '',
        appId: cfg.appId || cfg.AppId || '',
        appKey: cfg.appKey || cfg.AppKey || '',
        ssoKey: cfg.ssoKey || cfg.SsoKey || '',
        apiBaseUrl: cfg.apiBaseUrl || cfg.ApiBaseUrl || DEFAULT_API_BASE_URL,
        ssoPortalUrl: cfg.ssoPortalUrl || cfg.SsoPortalUrl || DEFAULT_SSO_PORTAL_URL,
        isEnabled: cfg.isEnabled != null ? cfg.isEnabled : (cfg.IsEnabled != null ? cfg.IsEnabled : true),
      };
    },
    /** 加载学校奥讯达配置 */
    loadAxdConfig() {
      if (!this.schoolDepartmentId) {
        this.axdConfig = null;
        this.resetConfigForm();
        return Promise.resolve();
      }
      this.configLoading = true;
      return getAxdSchoolConfig({ schoolDepartmentId: this.schoolDepartmentId }).then((res) => {
        this.configLoading = false;
        if (res.data.success) {
          this.axdConfig = res.data.response || null;
          this.applyConfigToForm(this.axdConfig);
          this.tabList.forEach((t) => this.updateBlockReason(t.key));
        }
      }).catch(() => {
        this.configLoading = false;
      });
    },
    buildConfigPayload() {
      return {
        SchoolDepartmentId: this.schoolDepartmentId,
        VendorSid: (this.configForm.vendorSid || '').trim(),
        AppId: (this.configForm.appId || '').trim(),
        AppKey: this.configForm.appKey,
        SsoKey: this.configForm.ssoKey,
        ApiBaseUrl: (this.configForm.apiBaseUrl || '').trim() || DEFAULT_API_BASE_URL,
        SsoPortalUrl: (this.configForm.ssoPortalUrl || '').trim() || DEFAULT_SSO_PORTAL_URL,
        IsEnabled: this.configForm.isEnabled,
      };
    },
    saveConfig() {
      if (!this.isSchoolNode) {
        this.$message.info('请选中学校节点后再保存配置');
        return;
      }
      if (!(this.configForm.vendorSid || '').trim() || !(this.configForm.appId || '').trim()) {
        this.$message.warning('sid 与 appid 不能为空');
        return;
      }
      if (!(this.configForm.appKey || '').trim()) {
        this.$message.warning('应用密钥不能为空');
        return;
      }
      this.configSaving = true;
      saveAxdSchoolConfig(this.buildConfigPayload()).then((res) => {
        this.configSaving = false;
        if (res.data.success) {
          this.$message.success(res.data.msg || '保存成功');
          this.axdConfig = res.data.response || null;
          this.applyConfigToForm(this.axdConfig);
          this.configExpanded = false;
          this.tabList.forEach((t) => this.updateBlockReason(t.key));
        } else {
          this.$message.error(res.data.msg || '保存失败');
        }
      }).catch(() => {
        this.configSaving = false;
        this.$message.error('保存请求失败');
      });
    },
    testConnection() {
      if (!this.isSchoolNode) {
        this.$message.info('请选中学校节点后再测试连通');
        return;
      }
      this.configTesting = true;
      testAxdConnection(this.buildConfigPayload()).then((res) => {
        this.configTesting = false;
        if (res.data.success) {
          this.$message.success(res.data.msg || '连接成功');
        } else {
          this.$message.error(res.data.msg || '连接失败');
        }
      }).catch(() => {
        this.configTesting = false;
        this.$message.error('测试请求失败');
      });
    },
    refreshAll() {
      this.tabList.forEach((t) => {
        this.loadStats(t.key);
      });
      this.reloadLogPanel(this.activeTab);
      this.loadCooldownForAllTabs();
      this.startCooldownTimer();
    },
    onTabChange() {
      this.reloadLogPanel(this.activeTab);
      this.loadStats(this.activeTab);
    },
    reloadLogPanel(tabKey) {
      if (tabKey && tabKey !== this.activeTab) return;
      this.$nextTick(() => {
        const panel = this.$refs.logPanel;
        if (panel && panel.resetAndLoad) {
          panel.resetAndLoad();
        }
      });
    },
    loadCooldownForAllTabs() {
      if (!this.currentDepartmentId) return;
      this.tabList.forEach((t) => {
        const config = axdSyncApiMap[t.key];
        if (!config || !config.getLogPage) return;
        config.getLogPage({
          rootDepartmentId: this.currentDepartmentId,
          pageIndex: 1,
          pageSize: 1,
        }).then((res) => {
          if (res.data.success && res.data.response) {
            const p = res.data.response;
            const rows = p.data != null ? p.data : p.Data;
            const latest = rows && rows.length ? normalizeAxdSyncLogPayload(rows[0]) : null;
            this.onHistoryLoaded(t.key, latest);
          }
        });
      });
    },
    loadStats(tabKey, manualRefresh = false) {
      if (!this.currentDepartmentId) {
        if (manualRefresh) {
          this.$message.info('请先选择左侧部门');
        }
        return;
      }
      const api = this.statsApiMap[tabKey];
      if (!api) return;
      api({ departmentId: this.currentDepartmentId }).then((res) => {
        if (res.data.success && res.data.response) {
          const r = res.data.response;
          const total = r.total != null ? r.total : r.Total;
          const synced = r.synced != null ? r.synced : r.Synced;
          const unsynced = r.unsynced != null ? r.unsynced : (r.Unsynced != null ? r.Unsynced : total - synced);
          this.stats[tabKey] = {
            total,
            synced,
            unsynced,
            tip: r.tip || r.Tip || '',
            classNotMappedCount: r.classNotMappedCount != null ? r.classNotMappedCount : (r.ClassNotMappedCount || 0),
            pendingDeleteCount: r.pendingDeleteCount != null ? r.pendingDeleteCount : (r.PendingDeleteCount || 0),
          };
          this.updateDeleteBlockReason(tabKey);
          if (manualRefresh) {
            const tab = this.tabList.find((t) => t.key === tabKey);
            const tabLabel = tab ? tab.label : '';
            this.reloadLogPanel(tabKey);
            if (this.statsDetailVisible && this.statsDetailTabKey === tabKey) {
              this.fetchStatsDetailPage();
            }
            this.$message.success(`${tabLabel}统计已刷新`);
          }
        } else if (manualRefresh) {
          this.$message.error(res.data.msg || '刷新统计失败');
        }
      }).catch(() => {
        if (manualRefresh) {
          this.$message.error('刷新统计失败');
        }
      });
    },
    onHistoryLoaded(tabKey, latestLog) {
      const remain = calcAxdCooldownRemainSeconds(latestLog);
      this.cooldownRemain[tabKey] = remain;
      this.updateBlockReason(tabKey);
    },
    updateBlockReason(tabKey) {
      if (!this.currentDepartmentId) {
        this.blockReason[tabKey] = '请先选择左侧部门';
        return;
      }
      if (!this.axdEnabled) {
        this.blockReason[tabKey] = '请先配置并启用学校奥讯达密钥';
        return;
      }
      if (tabKey === 'dept') {
        if (!this.isAxdDeptSyncNode) {
          this.blockReason[tabKey] = '请选择学校、校区、学段、年级或班级节点';
          return;
        }
      }
      const remain = this.cooldownRemain[tabKey];
      if (remain === -1) {
        this.blockReason[tabKey] = '当前存在进行中的同步任务，请稍后';
        return;
      }
      if (remain > 0) {
        this.blockReason[tabKey] = formatAxdCooldownText(remain);
        return;
      }
      this.blockReason[tabKey] = '';
      this.updateDeleteBlockReason(tabKey);
    },
    updateDeleteBlockReason(tabKey) {
      if (!this.currentDepartmentId || !this.axdEnabled) {
        this.deleteBlockReason[tabKey] = '';
        return;
      }
      if (tabKey === 'dept' && !this.isAxdDeptSyncNode) {
        this.deleteBlockReason[tabKey] = '';
        return;
      }
      const pending = (this.stats[tabKey] && this.stats[tabKey].pendingDeleteCount) || 0;
      if (pending <= 0) {
        this.deleteBlockReason[tabKey] = '当前范围内无已同步数据可删除';
        return;
      }
      this.deleteBlockReason[tabKey] = '';
    },
    canTriggerAction(tabKey) {
      return !this.blockReason[tabKey]
        && this.axdEnabled
        && !!this.currentDepartmentId
        && !this.syncLoading[tabKey]
        && !this.deleteLoading[tabKey];
    },
    canTriggerDelete(tabKey) {
      return this.canTriggerAction(tabKey) && ((this.stats[tabKey] && this.stats[tabKey].pendingDeleteCount) || 0) > 0;
    },
    triggerSync(tabKey) {
      if (!this.canTriggerAction(tabKey)) return;
      const config = axdSyncApiMap[tabKey];
      const deptName = this.currentDepartmentName || '';
      this.$confirm(config.confirmMessage.replace(/\{name\}/g, deptName), '提示', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
      }).then(() => {
        this.syncLoading[tabKey] = true;
        config.triggerSync({ Id: this.currentDepartmentId }).then((res) => {
          this.syncLoading[tabKey] = false;
          if (res.data.success) {
            this.$message.success(res.data.msg || '已提交同步任务');
            this.reloadLogPanel(tabKey);
            this.loadStats(tabKey);
          } else {
            this.$message.error(res.data.msg || '同步提交失败');
          }
        }).catch(() => {
          this.syncLoading[tabKey] = false;
          this.$message.error('同步请求失败');
        });
      }).catch(() => {});
    },
    triggerDelete(tabKey) {
      if (!this.canTriggerDelete(tabKey)) {
        if (this.deleteBlockReason[tabKey]) {
          this.$message.warning(this.deleteBlockReason[tabKey]);
        }
        return;
      }
      const config = axdSyncApiMap[tabKey];
      if (!config || !config.triggerDelete) return;
      const deptName = this.currentDepartmentName || '';
      const message = (config.deleteConfirmMessage || '').replace(/\{name\}/g, deptName);
      this.$confirm(message, '从奥讯达删除', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
      }).then(() => {
        this.deleteLoading[tabKey] = true;
        config.triggerDelete({ Id: this.currentDepartmentId }).then((res) => {
          this.deleteLoading[tabKey] = false;
          if (res.data.success) {
            this.$message.success(res.data.msg || '已提交删除任务');
            this.reloadLogPanel(tabKey);
            this.loadStats(tabKey);
          } else {
            this.$message.error(res.data.msg || '删除提交失败');
          }
        }).catch(() => {
          this.deleteLoading[tabKey] = false;
          this.$message.error('删除请求失败');
        });
      }).catch(() => {});
    },
    onSyncCompleted(tabKey) {
      this.loadStats(tabKey);
      this.reloadLogPanel(tabKey);
    },
    startCooldownTimer() {
      this.stopCooldownTimer();
      this.cooldownTimer = setInterval(() => {
        this.tabList.forEach((t) => {
          if (this.cooldownRemain[t.key] > 0) {
            this.cooldownRemain[t.key] -= 1;
          }
          this.updateBlockReason(t.key);
        });
      }, 1000);
    },
    stopCooldownTimer() {
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }
    },
    /** 统计卡片是否可点击查看明细 */
    canClickStatCard(type) {
      if (!this.currentDepartmentId) return false;
      const countMap = {
        synced: this.activeStats.synced,
        unsynced: this.activeStats.unsynced,
        total: this.activeStats.total,
      };
      return (countMap[type] || 0) > 0;
    },
    /** 打开统计明细分页弹窗 */
    openStatsDetail(type) {
      if (!this.canClickStatCard(type)) {
        if (!this.currentDepartmentId) {
          this.$message.info('请先选择左侧部门');
        } else {
          this.$message.info('暂无相关数据');
        }
        return;
      }
      this.statsDetailTabKey = this.activeTab;
      this.statsDetailFilterType = type;
      this.statsDetailPage = 1;
      this.statsDetailKeyword = '';
      this.statsDetailVisible = true;
      this.fetchStatsDetailPage();
    },
    onStatsDetailSearch() {
      this.statsDetailPage = 1;
      this.fetchStatsDetailPage();
    },
    fetchStatsDetailPage() {
      const config = this.statsDetailConfig;
      if (!config || !config.getStatsDetailPage || !this.currentDepartmentId) return;
      this.statsDetailLoading = true;
      const keyword = (this.statsDetailKeyword || '').trim();
      config.getStatsDetailPage({
        departmentId: this.currentDepartmentId,
        pageIndex: this.statsDetailPage,
        pageSize: this.statsDetailPageSize,
        syncFilter: this.statFilterMap[this.statsDetailFilterType] != null
          ? this.statFilterMap[this.statsDetailFilterType]
          : 0,
        keyword: keyword || undefined,
      }).then((res) => {
        this.statsDetailLoading = false;
        if (res.data.success && res.data.response) {
          const p = res.data.response;
          const rows = p.data != null ? p.data : p.Data;
          this.statsDetailTotal = p.dataCount != null ? p.dataCount : p.DataCount;
          const normalize = config.normalizeStatsDetail || ((r) => r);
          this.statsDetailList = (rows || []).map((r) => normalize(r));
          this.layoutStatsDetailTable();
        }
      }).catch(() => {
        this.statsDetailLoading = false;
        this.$message.error('加载明细失败');
      });
    },
    layoutStatsDetailTable() {
      const runLayout = () => {
        const table = this.$refs.statsDetailTable;
        if (table && typeof table.doLayout === 'function') {
          table.doLayout();
        }
      };
      this.$nextTick(runLayout);
      setTimeout(runLayout, 320);
    },
    onStatsDetailClosed() {
      this.statsDetailList = [];
      this.statsDetailTotal = 0;
      this.statsDetailPage = 1;
      this.statsDetailKeyword = '';
      this.statsDetailItemLoading = null;
    },
    getStatsDetailItemKey(row) {
      const config = this.statsDetailConfig;
      if (!config || !config.getDetailItemId || !row) return '';
      const id = config.getDetailItemId(row);
      return id != null ? String(id) : '';
    },
    getStatsDetailItemBlockReason(tabKey) {
      const blockReason = this.blockReason[tabKey] || '';
      if (!blockReason) return '';
      if (blockReason.includes('高峰')) return blockReason;
      if (blockReason.includes('进行中') || blockReason.includes('请稍候') || blockReason.includes('请稍后')) return blockReason;
      if (blockReason.includes('请先') || blockReason.includes('请选择')) return blockReason;
      return '';
    },
    canStatsDetailRowDelete(row) {
      const tabKey = this.statsDetailTabKey;
      if (!row || !row.isSynced) return false;
      if (!this.axdEnabled || !this.currentDepartmentId) return false;
      if (this.syncLoading[tabKey] || this.deleteLoading[tabKey]) return false;
      return !this.getStatsDetailItemBlockReason(tabKey);
    },
    canStatsDetailRowAction(row, action) {
      const tabKey = this.statsDetailTabKey;
      if (!row) return false;
      if (action === 'uploadPhoto') {
        if (tabKey !== 'student' || !row.isSynced) return false;
        if (!row.hasUploadableHeadPhoto) return false;
        if (!this.axdEnabled || !this.currentDepartmentId) return false;
        const blockReason = this.blockReason[tabKey] || '';
        if (blockReason.includes('高峰') || blockReason.includes('请先')) return false;
        return true;
      }
      if (action === 'delete') return this.canStatsDetailRowDelete(row);
      if (action === 'sync') {
        if (row.isSynced) return false;
        if (!this.axdEnabled || !this.currentDepartmentId) return false;
        if (this.syncLoading[tabKey] || this.deleteLoading[tabKey]) return false;
        return !this.getStatsDetailItemBlockReason(tabKey);
      }
      if (!this.canTriggerAction(tabKey)) return false;
      return false;
    },
    statsDetailSyncRow(row) {
      const tabKey = this.statsDetailTabKey;
      if (!this.canStatsDetailRowAction(row, 'sync')) {
        if (this.blockReason[tabKey]) this.$message.warning(this.blockReason[tabKey]);
        return;
      }
      const config = this.statsDetailConfig;
      const itemId = config.getDetailItemId(row);
      if (!itemId) return;
      this.$confirm('确认将该条数据同步到奥讯达吗？', '快速同步', { type: 'warning' })
        .then(() => {
          const loadingKey = this.getStatsDetailItemKey(row);
          this.statsDetailItemLoading = loadingKey;
          config.syncItem({ Id: itemId }).then((res) => {
            this.statsDetailItemLoading = null;
            if (res.data.success) {
              this.$message.success(res.data.msg || '已提交同步任务');
              this.fetchStatsDetailPage();
              this.loadStats(tabKey);
              this.reloadLogPanel(tabKey);
            } else {
              this.$message.error(res.data.msg || '同步提交失败');
            }
          }).catch(() => {
            this.statsDetailItemLoading = null;
            this.$message.error('同步请求失败');
          });
        }).catch(() => {});
    },
    statsDetailUploadPhotoRow(row) {
      const tabKey = this.statsDetailTabKey;
      if (!this.canStatsDetailRowAction(row, 'uploadPhoto')) {
        if (!row.hasUploadableHeadPhoto) {
          this.$message.warning('本地无可上传的 jpg 头像（需 wwwroot 下有效文件且不超过 500KB）');
          return;
        }
        const blockReason = this.blockReason[tabKey] || '';
        if (blockReason) this.$message.warning(blockReason);
        else if (!this.axdEnabled) this.$message.warning('请先配置并启用学校奥讯达密钥');
        return;
      }
      const config = this.statsDetailConfig;
      if (!config || !config.uploadPhotoItem) return;
      const itemId = config.getDetailItemId(row);
      if (!itemId) return;
      this.$confirm('确认将该学生的本地头像上传到奥讯达吗？<br/>仅上传头像，不更新学生基础信息。', '上传头像', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
      }).then(() => {
        const loadingKey = this.getStatsDetailItemKey(row);
        this.statsDetailItemLoading = loadingKey;
        config.uploadPhotoItem({ Id: itemId }).then((res) => {
          this.statsDetailItemLoading = null;
          if (res.data.success) {
            this.$message.success(res.data.msg || '头像已上传');
          } else {
            this.$message.error(res.data.msg || '头像上传失败');
          }
        }).catch(() => {
          this.statsDetailItemLoading = null;
          this.$message.error('头像上传请求失败');
        });
      }).catch(() => {});
    },
    statsDetailDeleteRow(row) {
      const tabKey = this.statsDetailTabKey;
      if (!this.canStatsDetailRowAction(row, 'delete')) {
        const reason = this.getStatsDetailItemBlockReason(tabKey) || this.blockReason[tabKey];
        if (reason) this.$message.warning(reason);
        return;
      }
      const config = this.statsDetailConfig;
      const itemId = config.getDetailItemId(row);
      if (!itemId) return;
      this.$confirm(
        '确认从奥讯达删除该项吗？<br/>仅删除<strong>厂家侧数据</strong>并清除<strong>同步映射</strong>，本地学生/家长/部门数据保留。',
        '快速删除',
        { type: 'warning', dangerouslyUseHTMLString: true },
      ).then(() => {
        const loadingKey = this.getStatsDetailItemKey(row);
        this.statsDetailItemLoading = loadingKey;
        config.deleteItem({ Id: itemId }).then((res) => {
          this.statsDetailItemLoading = null;
          if (res.data.success) {
            this.$message.success(res.data.msg || '已提交删除任务');
            this.fetchStatsDetailPage();
            this.loadStats(tabKey);
            this.reloadLogPanel(tabKey);
          } else {
            this.$message.error(res.data.msg || '删除提交失败');
          }
        }).catch(() => {
          this.statsDetailItemLoading = null;
          this.$message.error('删除请求失败');
        });
      }).catch(() => {});
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Outfit:wght@500;600;700&display=swap');

.tb-sync-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  background: #f8fafc;
  font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.tb-sync-topbar {
  flex-shrink: 0;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 14px 24px 0;
}

.tb-sync-topbar-title {
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  animation: pulse 2s infinite;
}

.pulse-dot.is-off {
  background: #94a3b8;
  box-shadow: none;
  animation: none;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

.tb-sync-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tb-sync-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 8px 8px 0 0;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: -1px;
}

.tb-sync-tab i {
  font-size: 15px;
  color: #94a3b8;
}

.tb-sync-tab:hover {
  color: #4f46e5;
  background: #f8fafc;
}

.tb-sync-tab:hover i {
  color: #4f46e5;
}

.tb-sync-tab.is-active {
  color: #4f46e5;
  font-weight: 600;
  background: linear-gradient(180deg, rgba(79, 70, 229, 0.06) 0%, transparent 100%);
  border-bottom-color: #4f46e5;
}

.tb-sync-tab.is-active i {
  color: #4f46e5;
}

.tb-sync-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 20px 24px 24px;
  overflow: hidden;
  background: #f8fafc;
}

.query-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
  gap: 20px;
  overflow: hidden;
}

.dept-filter-pane {
  position: relative;
  flex: 0 0 280px;
  width: 280px;
  min-width: 280px;
  flex-shrink: 0;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #6366f1;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  overflow: visible !important;
  transition: all 0.25s ease;
}

.dept-filter-pane.is-collapsed {
  flex: 0 0 0 !important;
  width: 0 !important;
  min-width: 0 !important;
  max-width: 0 !important;
  padding: 0 !important;
  border: none !important;
  margin: 0 !important;
  overflow: visible !important;
  box-shadow: none !important;
}

.dept-filter-pane.is-collapsed .dept-filter-header {
  display: none !important;
}

.dept-filter-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.dept-filter-header i {
  color: #6366f1;
}

.dept-filter-pane ::v-deep .department-pane {
  flex: 1;
  height: auto;
  min-height: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dept-filter-pane ::v-deep .department-pane .el-tree {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
}

.query-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #f1f5f9;
}

.maint-header-banner {
  flex: 0 0 auto;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
}

.maint-header-banner.is-collapsed {
  padding: 6px 12px;
  margin-bottom: 8px;
  background: #f8fafc;
}

.maint-header-banner:not(.is-collapsed) {
  padding: 10px 14px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
}

.banner-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 0;
  cursor: pointer;
  user-select: none;
  min-height: 28px;
}

.maint-header-banner:not(.is-collapsed) .banner-title-row {
  margin-bottom: 10px;
}

.banner-title-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.banner-collapse-icon {
  flex-shrink: 0;
  font-size: 13px;
  color: #94a3b8;
}

.banner-hint {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
}

.banner-icon {
  font-size: 15px;
  color: #6366f1;
}

.panel-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  line-height: 1.3;
}

.panel-desc {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.desc-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.desc-badge {
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  flex-shrink: 0;
}

.badge-primary {
  background: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
  border: 1px solid rgba(79, 70, 229, 0.2);
}

.badge-warning {
  background: rgba(217, 119, 6, 0.1);
  color: #d97706;
  border: 1px solid rgba(217, 119, 6, 0.2);
}

.badge-info {
  background: rgba(14, 165, 233, 0.1);
  color: #0284c7;
  border: 1px solid rgba(14, 165, 233, 0.2);
}

.config-card {
  border-left: 4px solid #7c3aed !important;
  margin-bottom: 8px;
}
.config-card.is-config-collapsed ::v-deep .el-card__body {
  display: none;
  padding: 0;
}

.config-card-header {
  cursor: pointer;
  user-select: none;
}

.config-card-header:hover .config-collapse-icon {
  color: #7c3aed;
}

.config-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.config-status-tag {
  margin-left: 8px;
  vertical-align: middle;
}

.config-collapsed-brief {
  margin-left: 10px;
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
}

.config-collapse-icon {
  font-size: 14px;
  color: #94a3b8;
  transition: color 0.15s;
}

.config-card-body {
  padding-top: 4px;
}

.config-form {
  padding-top: 4px;
}

.axd-config-form .axd-config-section {
  margin-bottom: 16px;
}

.axd-config-form .axd-config-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 3px solid #7c3aed;
}

.axd-config-secrets {
  padding: 14px 16px 4px;
  background: linear-gradient(180deg, #faf5ff 0%, #f8fafc 100%);
  border: 1px solid #e9d5ff;
  border-radius: 10px;
}

.axd-config-secrets-tip {
  margin: 0 0 12px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.axd-config-form .axd-secret-form-item {
  margin-bottom: 14px;
}

.axd-config-form .axd-secret-form-item ::v-deep .el-form-item__content {
  line-height: normal;
}

.axd-secret-input-wrap {
  position: relative;
  width: 100%;
}

.axd-secret-textarea ::v-deep .el-textarea__inner {
  padding: 8px 40px 8px 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  line-height: 1.55;
  word-break: break-all;
  border-radius: 8px;
  min-height: 36px !important;
}

.axd-secret-toggle-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  color: #64748b;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}

.axd-secret-toggle-btn:hover:not(:disabled) {
  color: #7c3aed;
  background: #fff;
}

.axd-secret-toggle-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.config-actions {
  display: flex;
  gap: 10px;
  padding-left: 88px;
  margin-top: 8px;
  padding-top: 4px;
  border-top: 1px solid #f1f5f9;
}

.config-hint-muted {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 400;
}

.config-notice {
  margin: 0;
}

.status-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 13px;
  color: #64748b;
}

.status-chip i {
  color: #94a3b8;
}

.status-chip strong {
  color: #1e293b;
  font-weight: 600;
}

.status-chip.chip-success {
  border-color: rgba(16, 185, 129, 0.3);
  background: rgba(16, 185, 129, 0.06);
}

.status-chip.chip-success i {
  color: #10b981;
}

.status-chip.chip-muted {
  background: #f1f5f9;
}

.notice-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  margin-bottom: 8px;
}

.notice-info {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.notice-warning {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}

.notice-muted {
  background: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.sync-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
  flex-shrink: 0;
  min-width: 0;
}
.sync-head-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  min-width: 0;
}
.sync-metrics {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;
}
.sync-head .status-strip {
  margin-bottom: 0;
  flex: 1 1 auto;
}
.sync-head-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}
.sync-head-tip {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #d97706;
}
.sync-head-tip.is-warning {
  color: #b45309;
}
.guide-block + .guide-block {
  margin-top: 14px;
}
.guide-text {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: #334155;
}

/* 手机端：顶栏/操作区换行，布局高度交给全局 query-layout 规则。最后修改：2026-08-31 */
@media (max-width: 768px) {
  .tb-sync-layout {
    height: auto;
    max-height: none;
    overflow: visible;
    border-radius: 0;
  }

  .tb-sync-topbar {
    padding: 12px 12px 0;
  }

  .tb-sync-topbar-title {
    font-size: 15px;
    margin-bottom: 8px;
  }

  .tb-sync-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2px;
  }

  .tb-sync-tab {
    padding: 8px 12px;
    font-size: 13px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .tb-sync-main {
    padding: 12px 10px 16px;
    overflow: visible;
    height: auto;
  }

  .query-layout {
    flex-direction: column;
    height: auto;
    overflow: visible;
    gap: 10px;
  }

  .dept-filter-pane {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    max-height: 280px;
    height: auto;
  }

  .query-main {
    height: auto;
    overflow: visible;
    padding-right: 0;
  }

  .log-card {
    flex: none;
    height: 420px;
    min-height: 350px;
  }

  .status-strip {
    flex-wrap: wrap;
    gap: 8px;
  }

  .action-row {
    flex-wrap: wrap;
    gap: 8px;
  }

  .action-row .el-button {
    margin-left: 0 !important;
  }

  .stats-detail-search-input {
    max-width: 100%;
  }

  .config-card-header {
    flex-wrap: wrap;
    gap: 8px;
  }
}

.stat-card {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  flex-shrink: 0;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
}

.stat-value {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-value .unit {
  font-size: 12px;
  margin-left: 4px;
  font-weight: 500;
}

.stat-icon-bg {
  display: none;
}

.stat-blue {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
}

.stat-emerald {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  color: #166534;
}

.stat-orange {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  color: #c2410c;
}

.stat-card-clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stat-card-clickable:hover {
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.stats-detail-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #64748b;
}

.stats-detail-meta i {
  margin-right: 4px;
  color: #94a3b8;
}

.stats-detail-tip {
  color: #d97706;
}

.stats-detail-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.stats-detail-search-input {
  flex: 1;
  max-width: 360px;
}

.stats-detail-table-wrap {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.stats-detail-table-wrap ::v-deep .el-table {
  width: 100%;
  border: none;
}

.stats-detail-table-wrap ::v-deep .el-table::before,
.stats-detail-table-wrap ::v-deep .el-table::after {
  display: none;
}

.stats-detail-table-wrap ::v-deep .el-table__header,
.stats-detail-table-wrap ::v-deep .el-table__body {
  width: 100% !important;
}

.stats-detail-table-wrap ::v-deep th:last-child,
.stats-detail-table-wrap ::v-deep td:last-child {
  border-right: none;
}

.stats-detail-table-wrap ::v-deep .el-table__body tr:last-child td {
  border-bottom: none;
}

.stats-detail-table-wrap ::v-deep .el-table__header th.gutter {
  display: none;
  width: 0 !important;
  min-width: 0 !important;
  border: none !important;
}

.stats-detail-table-wrap ::v-deep colgroup col[name="gutter"] {
  width: 0 !important;
}

.stats-detail-table ::v-deep .el-table__body td,
.stats-detail-table ::v-deep .el-table__header th {
  padding: 8px 10px;
}

.stats-detail-table ::v-deep .el-table__body td .cell,
.stats-detail-table ::v-deep .el-table__header th .cell {
  padding-left: 0;
  padding-right: 0;
}

.stats-detail-table ::v-deep .detail-id-cell-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stats-detail-op-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 2px;
}

.stats-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #d97706;
  margin-bottom: 8px;
  padding: 0 2px;
  flex-shrink: 0;
}

.step-card {
  border-radius: 10px !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02) !important;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.step-card:not(.log-card) ::v-deep .el-card__header {
  padding: 6px 12px;
}

.step-card:not(.log-card) ::v-deep .el-card__body {
  padding: 8px 12px;
}

/* 折叠配置：彻底隐藏 body，避免被上面 step-card padding 顶出空白。最后修改：2026-08-31 */
.config-card ::v-deep .el-card__header {
  padding: 5px 12px;
}

.config-card.is-config-collapsed ::v-deep .el-card__body {
  display: none !important;
  padding: 0 !important;
  height: 0 !important;
  border: none !important;
}

.branch-card {
  border-left: 4px solid #6366f1 !important;
}

.branch-card-teal {
  border-left: 4px solid #0d9488 !important;
}

.card-header-styled {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 13px;
  color: #1e293b;
}

.card-header-styled i {
  color: #6366f1;
}

.action-header {
  justify-content: space-between;
}

.action-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-hint {
  font-size: 12px;
  color: #94a3b8;
}

.action-hint-ok {
  color: #10b981;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.log-card {
  /* 吃满剩余高度；不够时不低于 350，由 query-main 整体滚动。最后修改：2026-08-31 */
  flex: 1 1 auto;
  min-height: 350px;
  height: 0;
  margin-bottom: 0 !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.log-card ::v-deep .el-card__header {
  flex-shrink: 0;
}

.log-card ::v-deep .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 8px;
}

.stats-detail-table .link-btn {
  margin: 0 !important;
  padding: 2px 6px !important;
  color: #4f46e5 !important;
  font-weight: 500;
}

.stats-detail-table .link-btn.link-btn-disabled,
.stats-detail-table .link-btn.is-disabled {
  color: #c0c4cc !important;
  cursor: not-allowed;
}

.stats-detail-table .link-btn-danger {
  color: #ef4444 !important;
}

/* 密钥：圆点隐藏，展开后等宽字体自动换行 */
.axd-secret-textarea.axd-secret-masked ::v-deep .el-textarea__inner {
  -webkit-text-security: disc !important;
}
</style>
